import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQPageSEO } from "@/components/seo/FAQPageSEO";
import PlayBadge from "@/components/PlayBadge";
import FAQAudioBlock from "@/components/FAQAudioBlock";

const faqData = [
  {
    question: "Proposez-vous un audit gratuit ?",
    answer: "Oui, nous proposons un audit complet gratuit de votre compte Amazon, incluant l'analyse de vos campagnes Ads, l'optimisation SEO de vos fiches produits, et des recommandations prioritaires d'amélioration."
  },
  {
    question: "Comment mesurez-vous les performances ?",
    answer: "Nous utilisons des KPIs précis : ROAS, conversion rate, rank organique, share of voice, Buy Box win rate. Reporting mensuel détaillé avec recommandations d'optimisation."
  },
  {
    question: "Travaillez-vous sur tous les pays européens ?",
    answer: "Oui, nous gérons vos comptes sur tous les marketplaces Amazon européens : FR, DE, IT, ES, UK, NL, etc. Stratégie locale adaptée à chaque marché."
  },
  {
    question: "Quels outils utilisez-vous ?",
    answer: "Nous utilisons les meilleurs outils du marché : Helium 10, Jungle Scout, SP API Amazon, Brand Analytics, DSP Amazon, ainsi que nos propres outils internes pour l'optimisation."
  },
  {
    question: "Comment garantissez-vous la conformité ?",
    answer: "Respect strict des TOS Amazon, veille réglementaire continue, processus de validation interne, et formation régulière de nos équipes aux évolutions des plateformes."
  }
];

export default function FAQ() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Audio Element */}
      <audio
        id="audio-methode"
        controls
        preload="metadata"
        crossOrigin="anonymous"
        playsInline
        src="https://symdojhcmtptpjipuwmp.supabase.co/storage/v1/object/public/media/faq-methode.mp3"
      />

      {/* SEO Meta Tags & Structured Data */}
      <FAQPageSEO
        title="FAQ — Cosmos Wave | Agence Amazon"
        description="Réponses aux questions fréquentes : audit gratuit, performance, multi-pays EU, outils, conformité Amazon SPN."
        canonical="https://www.cosmos-wave.com/faq"
        ogImage="https://www.cosmos-wave.com/og/cosmos-wave-faq-1200x630.jpg"
        keywords="FAQ Amazon, questions agence Amazon, audit gratuit Amazon, performance Amazon, multi-pays Europe"
        path="/faq"
        faqItems={faqData}
      />
      
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/10" />
        <div className="container mx-auto px-6 text-center relative">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
            <span className="text-primary font-medium text-sm">Questions & Réponses</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-primary/90 to-primary/70 bg-clip-text text-transparent">
              FAQ
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            Toutes les réponses sur nos services <span className="text-primary font-medium">Amazon SPN</span>, 
            notre approche et nos méthodes de travail.
          </p>

        </div>
      </section>

    {/* FAQ Audio Summary */}
    <FAQAudioBlock
      title="FAQ : Les réponses essentielles en 5 minutes"
      audioSrc="https://symdojhcmtptpjipuwmp.supabase.co/storage/v1/object/public/media/faq-methode.mp3"
      subtitle="Écoutez un résumé complet de nos services, méthodes et garanties"
      durationHintSec={300}
      audioId="audio-methode"
    />

    {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            
            {/* Services & Audit */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Badge variant="outline" className="px-3 py-1">Services</Badge>
                Audit & Approche
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="audit-gratuit" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Proposez-vous un audit gratuit ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Oui, nous proposons un audit 360 synthétique avec plan d'action priorisé. 
                    Cet audit couvre : analyse concurrentielle, optimisation SEO Amazon, 
                    performance Ads, santé catalogue et opportunités de croissance identifiées.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="performance" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Travaillez-vous à la performance ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Nous engageons la méthode et l'exécution sur des KPIs définis ensemble. 
                    Bonus à la performance en option selon les projets. Notre approche privilégie 
                    la croissance rentable et durable plutôt que les gains court-terme.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="delais" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Quels sont vos délais d'intervention ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Audit initial : 5-7 jours. Première optimisations : 2 semaines. 
                    Campagnes Ads : lancement sous 10 jours. Les premiers résultats 
                    sont généralement visibles sous 30 jours selon votre secteur.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Expertise Technique */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Badge variant="outline" className="px-3 py-1">Technique</Badge>
                Outils & Expertise
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="outils" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Quels outils utilisez-vous ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Stack technique professionnel : Amazon Ads Console, SP-API, GA4, 
                    BigQuery/Looker Studio, Helium 10, scripts Python pour l'automatisation, 
                    outils de bid management propriétaires et dashboards temps réel.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="multi-pays" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Gérez-vous le multi-pays EU ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Oui, expertise complète Europe : SEO/Ads localisés par pays, 
                    pricing dynamique, gestion logistique FBA Pan-EU, conformité 
                    réglementaire (CE, WEEE, emballages) et support multilingue.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="reporting" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Comment suivre les performances ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Dashboards temps réel connectés à vos données Amazon, rapports 
                    mensuels détaillés, alertes automatiques et points hebdomadaires. 
                    Transparence totale sur les actions et résultats.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Secteurs & Spécialisations */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Badge variant="outline" className="px-3 py-1">Business</Badge>
                Secteurs & Tarifs
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="secteurs" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Quels secteurs accompagnez-vous ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Spécialisations : DNVB/marques lifestyle, industriels B2B, 
                    cosmétique/beauté, food & beverage, tech/électronique, 
                    sport/outdoor. Expertise transverse applicable à tous secteurs.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="minimum" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Quel CA minimum pour travailler ensemble ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Minimum 50K€/mois de CA Amazon pour un accompagnement complet. 
                    Pour les projets plus petits : audit ponctuel ou formation 
                    équipes disponibles. L'objectif : ROI positif dès le premier mois.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="tarification" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Comment fonctionnent vos tarifs ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Forfait mensuel selon périmètre + % sur Ads spend géré. 
                    Pas d'engagement long terme : résultats et satisfaction d'abord. 
                    Devis personnalisé après audit gratuit.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="concurrence" className="border border-border/50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    Travaillez-vous avec des concurrents ?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Politique de confidentialité stricte. Maximum 1 client par 
                    niche très spécifique. Nous privilégions les partenariats 
                    long terme et l'exclusivité sectorielle quand c'est pertinent.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-primary/5 to-background relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative">
          <h2 className="text-3xl font-bold mb-4">
            Une question spécifique ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Échangeons sur votre projet Amazon. Audit gratuit et conseils personnalisés.
          </p>
          <Button 
            size="lg"
            className="text-base px-8 py-4 h-auto shadow-lg hover:shadow-xl transition-shadow duration-300"
            asChild
          >
            <Link to="/audit-gratuit">Planifier un échange</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}